// src/utils/dataRepair.ts
// Utility functions for repairing and validating patient data structures

import { supabase } from '../config/supabase';

export interface DataIntegrityReport {
  totalPatients: number;
  patientsNeedingRepair: number;
  patientsRepaired: number;
  errors: Array<{ patientId: string; error: string }>;
  details: Array<{
    patientId: string;
    patientName: string;
    issues: string[];
    repaired: boolean;
  }>;
}

export interface FinancialStructureIssue {
  patientId: string;
  patientName: string;
  missingFinanceiro: boolean;
  missingFaturas: boolean;
  missingControleFaltas: boolean;
}

export class DataRepairService {

  /**
   * Valida a estrutura financeira de um único paciente
   */
  static async validatePatientStructure(patientId: string): Promise<FinancialStructureIssue | null> {
    try {
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('patientFullName, first_name, last_name, financial_profiles(*)')
        .eq('patientId', patientId)
        .single();

      if (patientError) {
        if (patientError.code === 'PGRST116') { // Not Found
          return null;
        }
        throw patientError;
      }

      if (!patientData) {
        return null;
      }

      const patientName = patientData.patientFullName || `${patientData.first_name || 'Sem nome'} ${patientData.last_name || ''}`.trim();
      const financialProfile = patientData.financial_profiles;

      // Verificar se há faturas relacionadas a este paciente
      const { count: invoicesCount, error: invoicesError } = await supabase
        .from('invoices')
        .select('invoiceId', { count: 'exact' })
        .eq('patientId', patientId);

      if (invoicesError) throw invoicesError;

      // Verificar se há registro de faltas na tabela de appointments
      const { count: absenceCount, error: absenceError } = await supabase
        .from('appointments')
        .select('appointmentId', { count: 'exact' })
        .eq('patientId', patientId)
        .not('appointmentAbsenceInfo', 'is', null);

      if (absenceError) throw absenceError;

      const issues: FinancialStructureIssue = {
        patientId,
        patientName,
        missingFinanceiro: !financialProfile,
        missingFaturas: invoicesCount === 0,
        missingControleFaltas: absenceCount === 0
      };

      // Se não tem problemas, retornar null
      if (!issues.missingFinanceiro && !issues.missingFaturas && !issues.missingControleFaltas) {
        return null;
      }

      return issues;
    } catch (error: any) {
      console.error(`Erro ao validar paciente ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Repara a estrutura financeira de um único paciente
   */
  static async repairPatientStructure(patientId: string): Promise<boolean> {
    try {
      // Primeiro, verificar se o paciente existe e obter clientId
      const { data: patientData, error: patientError } = await supabase
        .from("patients")
        .select("patientFullName, first_name, last_name, clientId")
        .eq("patientId", patientId)
        .single();

      if (patientError) {
        if (patientError.code === "PGRST116") { // Not Found
          throw new Error(`Paciente ${patientId} não encontrado`);
        }
        throw patientError;
      }
      if (!patientData || !patientData.clientId) {
        throw new Error(`Dados do paciente ${patientId} ou clientId ausentes`);
      }
      const clientId = patientData.clientId;
      const patientName = patientData.patientFullName || `${patientData.first_name || 'Sem nome'} ${patientData.last_name || ''}`.trim();

      let repaired = false;

      // 1. Reparar financial_profiles (se ausente)
      const { data: financialProfile, error: fpError } = await supabase
        .from("financial_profiles")
        .select("financialProfileId")
        .eq("patientId", patientId)
        .eq("clientId", clientId)
        .single();

      if (fpError && fpError.code === "PGRST116") { // Not Found, precisa criar
        const { error: insertError } = await supabase.from("financial_profiles").insert({
          patientId: patientId,
          clientId: clientId,
          financialProfileIsActive: true,
          financialProfileHasPackage: false,
          financialProfileIndividualSessionPrice: 150,
        });
        if (insertError) throw insertError;
        console.log(`✅ Perfil financeiro criado para paciente ${patientId} (${patientName})`);
        repaired = true;
      } else if (fpError) {
        throw fpError;
      }

      // 2. Reparo para \'missingFaturas\' (invoices)
      // A lógica para reparo de faturas e controle de faltas será tratada em outro lugar
      // ou na criação de novos agendamentos/faturas, pois não são campos diretos no financial_profiles.
      // Por enquanto, esta função garante que o financial_profile exista.

      return repaired; // Retorna true se o perfil financeiro foi criado, false caso contrário.
    } catch (error: any) {
      console.error(`❌ Erro ao reparar paciente ${patientId}:`, error);
      throw error;
    }
  }

  /**
   * Escaneia todos os pacientes e identifica problemas na estrutura
   */
  static async scanAllPatients(): Promise<FinancialStructureIssue[]> {
    try {
      console.log("🔍 Iniciando escaneamento de todos os pacientes...");

      const { data: patients, error } = await supabase
        .from("patients")
        .select("patientId");

      if (error) throw error;

      const issues: FinancialStructureIssue[] = [];

      for (const patient of patients) {
        const issue = await this.validatePatientStructure(patient.patientId);
        if (issue) {
          issues.push(issue);
        }
      }

      console.log(`✅ Escaneamento completo: ${issues.length} pacientes com problemas encontrados`);
      return issues;
    } catch (error: any) {
      console.error("❌ Erro ao escanear pacientes:", error);
      throw error;
    }
  }

  /**
   * Repara todos os pacientes com problemas na estrutura
   */
  static async repairAllPatients(dryRun: boolean = true): Promise<DataIntegrityReport> {
    const report: DataIntegrityReport = {
      totalPatients: 0,
      patientsNeedingRepair: 0,
      patientsRepaired: 0,
      errors: [],
      details: []
    };

    try {
      console.log(dryRun ? '🔍 Modo simulação ativado' : '🔧 Modo reparação ativado');

      const { data: allPatients, error: fetchError } = await supabase
        .from("patients")
        .select("patientId, patientFullName, first_name, last_name");

      if (fetchError) throw fetchError;

      report.totalPatients = allPatients.length;
      console.log(`📊 Total de pacientes: ${report.totalPatients}`);

      for (const patient of allPatients) {
        const patientId = patient.patientId;
        const patientName = patient.patientFullName || `${patient.first_name || 'Sem nome'} ${patient.last_name || ''}`.trim();

        try {
          const issue = await this.validatePatientStructure(patientId);

          if (issue) {
            report.patientsNeedingRepair++;

            const issuesList = [];
            if (issue.missingFinanceiro) issuesList.push('Objeto financeiro ausente');
            if (issue.missingFaturas) issuesList.push('Faturas ausentes');
            if (issue.missingControleFaltas) issuesList.push('Controle de faltas ausente');

            if (dryRun) {
              // Modo simulação: apenas reportar
              report.details.push({
                patientId,
                patientName,
                issues: issuesList,
                repaired: false
              });
            } else {
              // Modo real: reparar
              const repaired = await this.repairPatientStructure(patientId);

              if (repaired) {
                report.patientsRepaired++;
                report.details.push({
                  patientId,
                  patientName,
                  issues: issuesList,
                  repaired: true
                });
              }
            }
          }
        } catch (error: any) {
          report.errors.push({
            patientId,
            error: error.message
          });
          console.error(`❌ Erro ao processar paciente ${patientId}:`, error);
        }
      }

      // Log do resumo
      console.log('\n📋 RESUMO DA OPERAÇÃO:');
      console.log(`   Total de pacientes: ${report.totalPatients}`);
      console.log(`   Pacientes com problemas: ${report.patientsNeedingRepair}`);
      console.log(`   Pacientes reparados: ${report.patientsRepaired}`);
      console.log(`   Erros: ${report.errors.length}`);

      return report;
    } catch (error: any) {
      console.error('❌ Erro ao reparar pacientes:', error);
      throw error;
    }
  }

  /**
   * Repara múltiplos pacientes em batch (mais eficiente)
   */
  static async batchRepairPatients(patientIds: string[]): Promise<{ success: number; failed: number }> {
    let success = 0;
    let failed = 0;

    try {
      console.log(`🔧 Reparando ${patientIds.length} pacientes em batch...`);

      for (const patientId of patientIds) {
        try {
          const repaired = await this.repairPatientStructure(patientId);
          if (repaired) {
            success++;
          }
        } catch (error: any) {
          console.error(`❌ Erro ao reparar paciente ${patientId} no batch:`, error);
          failed++;
        }
      }

      console.log(`✅ Reparo em batch concluído: ${success} pacientes reparados, ${failed} falharam.`);
      return { success, failed };
    } catch (error: any) {
      console.error('❌ Erro ao executar batch repair:', error);
      throw error;
    }
  }

  /**
   * Gera relatório detalhado de integridade dos dados
   */
  static async generateIntegrityReport(): Promise<DataIntegrityReport> {
    console.log('📊 Gerando relatório de integridade dos dados...');
    return await this.repairAllPatients(true); // Dry run
  }

  /**
   * Executa reparo completo de todos os pacientes
   */
  static async executeFullRepair(): Promise<DataIntegrityReport> {
    console.log('🔧 Executando reparo completo de todos os pacientes...');
    return await this.repairAllPatients(false); // Real repair
  }
}
